import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarCheck, CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { SectionHeading, slugify } from "@/pages/PublicCatalog";

const WEEK_ORDER = [
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
  "Domingo",
];

const selectCls =
  "h-10 w-full border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary";

const EMPTY_FORM = { full_name: "", birth_date: "", whatsapp: "", email: "", city: "", public_note: "" };

function calcAge(iso) {
  if (!iso) return null;
  const b = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age;
}

function Horarios({ courses, modalities, selection, setSelection }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!selection.courseId || !selection.modalityId) {
      setSlots([]);
      setFetched(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    api
      .get("/public/availability", {
        params: { course_id: selection.courseId, modality_id: selection.modalityId },
      })
      .then((r) => {
        if (active) {
          setSlots(r.data);
          setFetched(true);
        }
      })
      .catch(() => {
        if (active) {
          setSlots([]);
          setFetched(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selection.courseId, selection.modalityId]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of slots) (g[s.weekday_name] ||= []).push(s);
    return WEEK_ORDER.filter((d) => g[d]).map((d) => [d, g[d]]);
  }, [slots]);

  const choose = (s) => {
    setSelection((p) => ({ ...p, schedule: `${s.weekday_name}, das ${s.start_time} às ${s.end_time}` }));
    document.getElementById("interesse")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="horarios" className="border-y border-border bg-zinc-50 py-20 lg:py-28" data-testid="horarios-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading eyebrow="Horários" title="Horários livres neste momento" testId="horarios-title" />
        <p className="mt-4 max-w-2xl text-base text-muted-foreground">
          Escolha o curso e a modalidade para ver, em tempo real, os horários disponíveis para começar suas aulas.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="schedule-course">Curso</Label>
            <select
              id="schedule-course"
              className={selectCls}
              value={selection.courseId}
              onChange={(e) => setSelection((p) => ({ ...p, courseId: e.target.value, schedule: "" }))}
              data-testid="schedule-course-select"
            >
              <option value="">Selecione o curso</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="schedule-modality">Modalidade</Label>
            <select
              id="schedule-modality"
              className={selectCls}
              value={selection.modalityId}
              onChange={(e) => setSelection((p) => ({ ...p, modalityId: e.target.value, schedule: "" }))}
              data-testid="schedule-modality-select"
            >
              <option value="">Selecione a modalidade</option>
              {modalities.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} — {m.duration_minutes} min
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-8" data-testid="schedule-results">
          {loading && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="schedule-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Consultando horários...
            </p>
          )}
          {!loading && fetched && grouped.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="schedule-empty">
              Nenhum horário livre no momento para esta combinação. Envie o formulário abaixo — a secretaria
              encontrará uma alternativa para você.
            </p>
          )}
          {!loading &&
            grouped.map(([day, daySlots]) => (
              <div key={day} className="mt-6 first:mt-0" data-testid={`schedule-day-${slugify(day)}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{day}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {daySlots.map((s) => (
                    <button
                      key={`${s.weekday}-${s.start_time}`}
                      type="button"
                      onClick={() => choose(s)}
                      className="border border-foreground bg-white px-4 py-2 text-sm font-semibold transition-colors hover:border-primary hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid={`slot-${s.weekday}-${s.start_time.replace(":", "")}`}
                    >
                      {s.start_time} – {s.end_time}
                    </button>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
}

function InterestForm({ courses, modalities, settings, selection, setSelection }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ type: null, msg: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const age = calcAge(form.birth_date);
  const minAge = settings?.min_age ?? 7;
  const waDigits = (settings?.whatsapp || "").replace(/\D/g, "");
  const waLink = waDigits ? `https://wa.me/${waDigits.startsWith("55") ? waDigits : `55${waDigits}`}` : "#";

  const submit = async (e) => {
    e.preventDefault();
    const waDigitsOnly = form.whatsapp.replace(/\D/g, "");
    if (waDigitsOnly.length < 10) {
      setStatus({ type: "error", msg: "Informe um WhatsApp válido com DDD. Ex.: (11) 99999-9999" });
      return;
    }
    setSending(true);
    setStatus({ type: null, msg: "" });
    try {
      const { data } = await api.post("/public/leads", {
        full_name: form.full_name.trim(),
        birth_date: form.birth_date,
        whatsapp: form.whatsapp,
        email: form.email.trim() || null,
        city: form.city.trim() || null,
        course_id: selection.courseId,
        modality_id: selection.modalityId,
        requested_schedule: selection.schedule || null,
        public_note: form.public_note.trim() || null,
        origin: "Site",
      });
      setStatus({ type: "success", msg: data.message });
      setForm(EMPTY_FORM);
      setSelection({ courseId: "", modalityId: "", schedule: "" });
    } catch (err) {
      setStatus({ type: "error", msg: formatApiError(err) });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="interesse" className="bg-[#0A0A0A] py-20 text-white lg:py-28" data-testid="interesse-section">
      <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <SectionHeading eyebrow="Matrículas abertas" title="Quero estudar no IMJ" dark testId="interesse-title" />
          <p className="mt-4 text-base text-zinc-300">
            Preencha o formulário e nossa secretaria entrará em contato pelo WhatsApp para confirmar seu horário e
            finalizar sua matrícula.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-zinc-300">
            <li className="flex gap-3">
              <CheckCircle2 size={18} className="shrink-0 text-primary" /> Sem taxa de matrícula
            </li>
            <li className="flex gap-3">
              <CheckCircle2 size={18} className="shrink-0 text-primary" /> Aulas individuais, no seu ritmo
            </li>
            <li className="flex gap-3">
              <CheckCircle2 size={18} className="shrink-0 text-primary" /> Mensalidade fixa, sem surpresas
            </li>
          </ul>
          <Button
            asChild
            variant="outline"
            className="mt-8 border-zinc-600 bg-transparent font-semibold text-white hover:bg-zinc-800 hover:text-white"
            data-testid="interest-whatsapp-button"
          >
            <a href={waLink} target="_blank" rel="noreferrer">
              <MessageCircle size={18} className="mr-2" /> Falar no WhatsApp
            </a>
          </Button>
        </div>

        <form onSubmit={submit} className="border border-zinc-800 bg-white p-6 text-foreground sm:p-8" data-testid="interest-form">
          {status.type === "success" ? (
            <div className="flex h-full flex-col items-start justify-center py-10" data-testid="interest-success-alert">
              <CheckCircle2 size={40} className="text-primary" />
              <h3 className="mt-4 font-heading text-2xl font-bold">Interesse registrado!</h3>
              <p className="mt-2 text-sm text-zinc-600">{status.msg}</p>
              <Button
                type="button"
                variant="outline"
                className="mt-6 font-semibold"
                onClick={() => setStatus({ type: null, msg: "" })}
                data-testid="interest-again-button"
              >
                Enviar outro interesse
              </Button>
              <Button
                asChild
                className="mt-3 bg-primary font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                data-testid="interest-success-whatsapp"
              >
                <a href={waLink} target="_blank" rel="noreferrer">
                  <MessageCircle size={16} className="mr-2" /> Chamar a secretaria no WhatsApp
                </a>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {status.type === "error" && (
                <div
                  className="flex items-start gap-2 border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
                  data-testid="interest-error-alert"
                >
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="interest-name">Nome completo *</Label>
                <Input
                  id="interest-name"
                  required
                  minLength={3}
                  value={form.full_name}
                  onChange={set("full_name")}
                  data-testid="interest-name-input"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="interest-birthdate">Data de nascimento *</Label>
                  <Input
                    id="interest-birthdate"
                    type="date"
                    required
                    value={form.birth_date}
                    onChange={set("birth_date")}
                    data-testid="interest-birthdate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="interest-whatsapp">WhatsApp *</Label>
                  <Input
                    id="interest-whatsapp"
                    required
                    placeholder="(11) 99999-9999"
                    value={form.whatsapp}
                    onChange={set("whatsapp")}
                    data-testid="interest-whatsapp-input"
                  />
                </div>
              </div>
              {age !== null && age >= 0 && age < minAge && (
                <p className="border border-primary/50 bg-primary/10 p-3 text-xs text-zinc-700" data-testid="interest-age-notice">
                  Para alunos com menos de {minAge} anos, a matrícula passa por uma análise da escola antes da
                  confirmação.
                </p>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="interest-email">E-mail</Label>
                  <Input
                    id="interest-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="interest-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="interest-city">Cidade</Label>
                  <Input
                    id="interest-city"
                    value={form.city}
                    onChange={set("city")}
                    data-testid="interest-city-input"
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="interest-course">Curso *</Label>
                  <select
                    id="interest-course"
                    required
                    className={selectCls}
                    value={selection.courseId}
                    onChange={(e) => setSelection((p) => ({ ...p, courseId: e.target.value }))}
                    data-testid="interest-course-select"
                  >
                    <option value="">Selecione</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="interest-modality">Modalidade *</Label>
                  <select
                    id="interest-modality"
                    required
                    className={selectCls}
                    value={selection.modalityId}
                    onChange={(e) => setSelection((p) => ({ ...p, modalityId: e.target.value }))}
                    data-testid="interest-modality-select"
                  >
                    <option value="">Selecione</option>
                    {modalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} — {m.duration_minutes} min
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="interest-schedule">Horário de preferência</Label>
                <Input
                  id="interest-schedule"
                  placeholder="Ex.: Segunda-feira, das 14:00 às 14:50"
                  value={selection.schedule}
                  onChange={(e) => setSelection((p) => ({ ...p, schedule: e.target.value }))}
                  data-testid="interest-schedule-input"
                />
                <p className="text-xs text-muted-foreground">
                  Escolha um horário na seção acima ou descreva sua preferência.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="interest-note">Mensagem (opcional)</Label>
                <textarea
                  id="interest-note"
                  rows={3}
                  maxLength={500}
                  value={form.public_note}
                  onChange={set("public_note")}
                  className="w-full border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Já estudou música? Conte um pouco sobre você."
                  data-testid="interest-note-input"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-primary py-6 font-heading text-base font-bold text-primary-foreground hover:bg-primary/90"
                data-testid="interest-submit-button"
              >
                {sending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CalendarCheck size={18} className="mr-2" />
                )}
                Enviar interesse
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Seus dados são usados apenas para contato da secretaria do IMJ.
              </p>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

export default function PublicInterest(props) {
  return (
    <>
      <Horarios {...props} />
      <InterestForm {...props} />
    </>
  );
}
